import { useRef, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import axios from "axios";

import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { BarrioContext } from "../components/BarrioContext";
import { useAuth } from "../components/AuthContext";
import { useTerminologia } from "../hooks/useTerminologia";

import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';

const PlanPago = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();
    const [loadingBackdrop, setLoadingBackdrop] = useState(false);
    const [unidadesFuncionales, setUnidadesFuncionales] = useState([]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(res => setUnidadesFuncionales(res.data))
            .catch(err => console.error('Error cargando unidades funcionales:', err));
    }, [barrio?.id, administracionId]);

    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(location.state?.selectedDate ?? new Date());

    // ─── Helpers ────────────────────────────────────────────────────────────────

    const formatDate = (date) => {
        if (!date) return "";
        const year  = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day   = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const camelToHeader = (key) =>
        key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();

    const formatValue = (value, type) => {
        if (value === null || value === undefined) return "";
        switch (type) {
            case "date":
                return new Date(value).toLocaleDateString("es-AR");
            case "currency":
                return typeof value === "number"
                    ? value.toLocaleString("es-AR", { style: "currency", currency: "ARS" })
                    : value;
            default:
                return typeof value === "object" ? JSON.stringify(value) : value;
        }
    };

    const flattenRow = (row) => {
        const flat = {};
        for (const [key, value] of Object.entries(row)) {
            if (typeof value === "object" && value !== null && !key.endsWith("_type")) {
                for (const [nestedKey, nestedValue] of Object.entries(value)) {
                    flat[nestedKey] = nestedValue;
                    flat[`${nestedKey}_type`] = row[`${key}_type`] ?? "currency";
                }
            } else {
                flat[key] = value;
            }
        }
        return flat;
    };

    // ─── Estado: Nuevo Plan de Pago ───────────────────────────────────────────────

    const initialFormData = {
        unidadFuncionalId: "",
        concepto: "",
        importeTotal: "",
        cantidadCuotas: 1,
    };

    const [formData, setFormData] = useState(initialFormData);
    const [visible, setVisible] = useState(false);
    const [planes, setPlanes] = useState([]);
    const [filtroUF, setFiltroUF] = useState(null);

    const handleChange = (campo, valor) => {
        setFormData((prev) => ({ ...prev, [campo]: valor }));
    };

    // ─── Submit ─────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            periodo: formatDate(selectedDate),
            administracionId,
        };
        try {
            await axios.post(`${HOST_API}api/PlanPago/SavePlanPago`, payload);
            setVisible(false);
            fetchPlanes();
        } catch (err) {
            console.error(err);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar el Plan de Pago', life: 4000 });
        }
    };

    // ─── Fetch planes ───────────────────────────────────────────────────────────

    const fetchPlanes = useCallback(async () => {
        setLoadingBackdrop(true);
        try {
            let url = `${HOST_API}api/PlanPago/GetPlanesPago?administracionId=${administracionId}`;
            if (filtroUF) url += `&unidadFuncionalId=${filtroUF}`;
            const response = await axios.get(url);
            setPlanes(response.data);
        } catch (err) {
            console.error(err.message || "Error al obtener los planes de pago");
        } finally {
            setLoadingBackdrop(false);
        }
    }, [filtroUF, administracionId]);

    useEffect(() => {
        fetchPlanes();
    }, [fetchPlanes]);

    const planesFlat = useMemo(() => planes?.map(flattenRow) ?? [], [planes]);

    const columns = useMemo(() => {
        if (!planesFlat?.length) return [];
        const firstRow = planesFlat[0];
        return Object.keys(firstRow)
            .filter((key) => !key.endsWith("_type") && key !== 'planPagoId' && key !== 'administracionId')
            .map((field) => ({
                field,
                type: firstRow[`${field}_type`] ?? "text",
                header: camelToHeader(field),
            }));
    }, [planes]);

    // ─── Cancelar plan ──────────────────────────────────────────────────────────

    const handleCancelar = (planPagoId) => {
        confirmDialog({
            message: '¿Estás seguro de cancelar este plan de pago?',
            header: 'Confirmar cancelación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Cancelar plan',
            rejectLabel: 'Volver',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/PlanPago/CancelarPlanPago?planPagoId=${planPagoId}&administracionId=${administracionId}`);
                    fetchPlanes();
                } catch (err) {
                    console.error(err);
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cancelar el plan de pago', life: 3000 });
                }
            },
        });
    };

    // ─── Cuotas del plan seleccionado ───────────────────────────────────────────

    const [cuotasVisible, setCuotasVisible] = useState(false);
    const [cuotasPlan, setCuotasPlan] = useState(null); // fila del plan seleccionado
    const [cuotas, setCuotas] = useState([]);
    const [loadingCuotas, setLoadingCuotas] = useState(false);

    const handleVerCuotas = async (row) => {
        setCuotasPlan(row);
        setCuotasVisible(true);
        setLoadingCuotas(true);
        try {
            const response = await axios.get(
                `${HOST_API}api/PlanPago/GetPlanPagoCuotas?administracionId=${administracionId}&planPagoId=${row.planPagoId}`
            );
            setCuotas(response.data);
        } catch (err) {
            console.error(err);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron obtener las cuotas del plan', life: 3000 });
        } finally {
            setLoadingCuotas(false);
        }
    };

    const cuotasFlat = useMemo(() => cuotas?.map(flattenRow) ?? [], [cuotas]);

    const columnsCuotas = useMemo(() => {
        if (!cuotasFlat?.length) return [];
        const firstRow = cuotasFlat[0];
        return Object.keys(firstRow)
            .filter((key) => !key.endsWith("_type") && key !== 'planPagoId' && key !== 'administracionId' && key !== 'unidadFuncional' && key !== 'propietario' && key !== 'concepto')
            .map((field) => ({
                field,
                type: firstRow[`${field}_type`] ?? "text",
                header: camelToHeader(field),
            }));
    }, [cuotas]);

    // ─── Render ─────────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Planes de Pago</h4>
            <br />

            <ConfirmDialog />
            <Toast ref={toast} />
            <Dialog
                visible={loadingBackdrop}
                modal
                closable={false}
                showHeader={false}
                className="backdrop-dialog"
            >
                <ProgressSpinner style={{ width: '60px', height: '60px' }} />
            </Dialog>

            <div className="flex align-items-center gap-3 mb-3 flex-wrap">
                <Button
                    label="Nuevo Plan de Pago"
                    icon="pi pi-plus"
                    onClick={() => { setFormData(initialFormData); setVisible(true); }}
                />
                <Dropdown
                    value={filtroUF}
                    options={unidadesFuncionales}
                    optionLabel="unidadFuncional"
                    optionValue="unidadFuncionalId"
                    onChange={(e) => setFiltroUF(e.value)}
                    placeholder="Todas las UF"
                    showClear
                    filter
                    className="p-inputtext-sm"
                    style={{ width: 220 }}
                />
            </div>

            <Dialog
                header="Nuevo Plan de Pago"
                visible={visible}
                style={{ width: "60vw" }}
                breakpoints={{ "960px": "100vw" }}
                modal
                onHide={() => setVisible(false)}
            >
                <form className="p-fluid grid" onSubmit={handleSubmit}>
                    <div className="col-12">
                        <div className="col-12 flex flex-wrap gap-3 mt-3">

                            <div className="w-full md:w-4">
                                <label>{t.UnidadFuncional}</label>
                                <Dropdown
                                    value={formData.unidadFuncionalId || null}
                                    options={unidadesFuncionales}
                                    optionLabel="unidadFuncional"
                                    optionValue="unidadFuncionalId"
                                    onChange={(e) => handleChange("unidadFuncionalId", e.value)}
                                    placeholder={`Seleccionar ${t.UF}`}
                                    className="p-inputtext-sm w-full"
                                    filter
                                />
                            </div>

                            <div className="w-full md:w-4">
                                <label>Período inicial</label>
                                <Calendar
                                    value={selectedDate}
                                    view="month"
                                    dateFormat="mm/yy"
                                    onChange={(e) => setSelectedDate(e.value)}
                                    showIcon
                                    className="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="w-full md:w-4">
                                <label>Cantidad de Cuotas</label>
                                <InputText
                                    type="number"
                                    min="1"
                                    value={formData.cantidadCuotas || 1}
                                    onChange={(e) => handleChange("cantidadCuotas", e.target.value)}
                                    className="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="w-full md:w-6">
                                <label>Concepto</label>
                                <InputText
                                    type="text"
                                    value={formData.concepto || ""}
                                    onChange={(e) => handleChange("concepto", e.target.value)}
                                    className="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="w-full md:w-4">
                                <label>Importe Total</label>
                                <InputText
                                    type="number"
                                    step="0.01"
                                    value={formData.importeTotal || ""}
                                    onChange={(e) => handleChange("importeTotal", e.target.value)}
                                    className="p-inputtext-sm w-full"
                                />
                            </div>

                            <div className="col-12 mt-3">
                                <Button label="Guardar" icon="pi pi-save" type="submit" />
                            </div>

                        </div>
                    </div>
                </form>
            </Dialog>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={planesFlat}
                        paginator
                        rows={10}
                        showGridlines
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        responsiveLayout="scroll"
                        emptyMessage="No hay planes de pago cargados"
                        className="p-datatable-gridlines bg-white"
                    >
                        {columns.map(({ field, header, type }) => (
                            <Column
                                key={field}
                                field={field}
                                header={header}
                                sortable
                                body={(row) => formatValue(row[field], type)}
                            />
                        ))}
                        <Column
                            header="Acciones"
                            style={{ width: '10rem' }}
                            body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-list" rounded text severity="info" onClick={() => handleVerCuotas(row)} tooltip="Ver cuotas" />
                                    <Button icon="pi pi-ban" rounded className="btn-delete" onClick={() => handleCancelar(row.planPagoId)} tooltip="Cancelar plan" />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>

            {/* ─── Dialog: cuotas del plan seleccionado ─────────────────────────── */}
            <Dialog
                header={cuotasPlan ? `Cuotas — UF ${cuotasPlan.unidadFuncional} · ${cuotasPlan.concepto}` : 'Cuotas'}
                visible={cuotasVisible}
                style={{ width: "70vw" }}
                breakpoints={{ "960px": "100vw" }}
                modal
                onHide={() => setCuotasVisible(false)}
            >
                {loadingCuotas ? (
                    <div className="flex justify-content-center p-4">
                        <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                    </div>
                ) : (
                    <DataTable
                        value={cuotasFlat}
                        paginator
                        rows={12}
                        showGridlines
                        responsiveLayout="scroll"
                        emptyMessage="Este plan no tiene cuotas registradas"
                        className="p-datatable-gridlines bg-white"
                    >
                        {columnsCuotas.map(({ field, header, type }) => (
                            <Column
                                key={field}
                                field={field}
                                header={header}
                                sortable
                                body={(row) => formatValue(row[field], type)}
                            />
                        ))}
                    </DataTable>
                )}
            </Dialog>
        </>
    );
};

export default PlanPago;
